// Race setup and challenge list pages, built on the settings page component.

import type { RaceConfig } from '../race/race';
import { CHALLENGES, type Challenge } from '../race/challenges';
import { COMPOUNDS, type Compound } from '../race/tyres';
import { WEATHER_LABELS, type WeatherPreset } from '../race/weather';
import { wingLabel, type CarSetup } from '../race/setup';
import { SettingsPage } from './settings';
import { formatTime } from './hud';

export type WeatherChoice = WeatherPreset | 'random';

export interface RaceChoices {
  laps: number;
  weather: WeatherChoice;
  startCompound: Compound;
  mandatoryCompounds: boolean;
  damage: boolean;
}

const LAP_OPTIONS = [3, 5, 8, 10, 15, 20, 28, 56];
const WEATHER_OPTIONS: WeatherChoice[] = ['dry', 'changeable', 'shower', 'wet-start', 'storm', 'random'];
const START_TYRES: Compound[] = ['soft', 'medium', 'hard', 'inter', 'wet'];
const KEY = 'fone.race';

export function loadChoices(): RaceChoices {
  const d: RaceChoices = { laps: 10, weather: 'changeable', startCompound: 'medium', mandatoryCompounds: true, damage: true };
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return d;
    const v = JSON.parse(raw) as Partial<RaceChoices>;
    return {
      laps: LAP_OPTIONS.includes(v.laps ?? 0) ? v.laps! : d.laps,
      weather: WEATHER_OPTIONS.includes(v.weather as WeatherChoice) ? (v.weather as WeatherChoice) : d.weather,
      startCompound: START_TYRES.includes(v.startCompound as Compound) ? (v.startCompound as Compound) : d.startCompound,
      mandatoryCompounds: v.mandatoryCompounds ?? true,
      damage: v.damage ?? true,
    };
  } catch {
    return d;
  }
}

function saveChoices(c: RaceChoices): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(c));
  } catch {
    // Not persisted.
  }
}

/** Resolve choices into a race config (a random weather pick is fixed by the seed). */
export function toConfig(c: RaceChoices, setup: CarSetup, seed = Math.floor(Math.random() * 1e6)): RaceConfig {
  const presets: WeatherPreset[] = ['dry', 'dry', 'changeable', 'shower', 'wet-start', 'storm'];
  const weather = c.weather === 'random' ? presets[seed % presets.length]! : c.weather;
  return { laps: c.laps, weather, seed, startCompound: c.startCompound, mandatoryCompounds: c.mandatoryCompounds, damage: c.damage, setup: { ...setup } };
}

const cycle = <T>(list: T[], v: T, d = 1): T => list[(list.indexOf(v) + d + list.length) % list.length]!;

export function buildRaceSetup(
  root: HTMLElement,
  setup: CarSetup,
  onSetupChange: () => void,
  onStart: (choices: RaceChoices) => void,
  onBack: () => void,
): SettingsPage {
  const c = loadChoices();
  const change = (): void => saveChoices(c);
  const page = new SettingsPage(
    root,
    [
      { id: 'laps', label: 'Laps', hint: 'Shorter races wear tyres faster', value: () => `${c.laps}`, dec: () => ((c.laps = cycle(LAP_OPTIONS, c.laps, -1)), change()), inc: () => ((c.laps = cycle(LAP_OPTIONS, c.laps)), change()) },
      { id: 'weather', label: 'Weather', value: () => (c.weather === 'random' ? 'Surprise me' : WEATHER_LABELS[c.weather]), cycle: () => ((c.weather = cycle(WEATHER_OPTIONS, c.weather)), change()) },
      { id: 'tyre', label: 'Starting tyres', value: () => `${COMPOUNDS[c.startCompound].name} (${COMPOUNDS[c.startCompound].pirelli})`, cycle: () => ((c.startCompound = cycle(START_TYRES, c.startCompound)), change()) },
      { id: 'rule', label: 'Two-compound rule', hint: 'Must run 2 dry compounds (B6.3.6)', value: () => (c.mandatoryCompounds ? 'On' : 'Off'), cycle: () => ((c.mandatoryCompounds = !c.mandatoryCompounds), change()) },
      { id: 'wing', label: 'Wing level', hint: 'More grip vs top speed', value: () => wingLabel(setup.wing), dec: () => ((setup.wing = Math.max(-2, setup.wing - 1)), onSetupChange()), inc: () => ((setup.wing = Math.min(2, setup.wing + 1)), onSetupChange()) },
      { id: 'bias', label: 'Brake bias', hint: 'Front share', value: () => `${Math.round(setup.bias * 100)}%`, dec: () => ((setup.bias = Math.max(0.5, Math.round((setup.bias - 0.01) * 100) / 100)), onSetupChange()), inc: () => ((setup.bias = Math.min(0.62, Math.round((setup.bias + 0.01) * 100) / 100)), onSetupChange()) },
      { id: 'damage', label: 'Damage', hint: 'Hits cost downforce', value: () => (c.damage ? 'On' : 'Off'), cycle: () => ((c.damage = !c.damage), change()) },
    ],
    [
      { label: 'Back', kind: 'secondary', run: () => undefined, closes: true },
      { label: 'Start race', kind: 'primary', run: () => onStart(c) },
    ],
    'Race',
  );
  page.onClose = onBack;
  return page;
}

export function buildChallenges(root: HTMLElement, bestOf: (c: Challenge) => { medal?: string; total?: number }, onPlay: (c: Challenge) => void, onBack: () => void): SettingsPage {
  const page = new SettingsPage(
    root,
    CHALLENGES.map((ch) => ({
      id: ch.id,
      label: ch.name,
      hint: ch.description,
      value: () => {
        const b = bestOf(ch);
        return b.medal ? `${b.medal[0]!.toUpperCase()}${b.medal.slice(1)} · ${formatTime(b.total)}` : b.total ? formatTime(b.total) : 'Play';
      },
      cycle: () => onPlay(ch),
    })),
    [{ label: 'Back', kind: 'secondary', run: () => undefined, closes: true }],
    'Challenges',
    'one-column',
  );
  page.onClose = onBack;
  return page;
}
